// @ts-check
import React, { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { licenseService } from './apiService';
import { Modal } from './components/Modal';
import { Button } from './components/Button';
import { Badge } from './components/Badge';
import './LicenseExtensionModal.css';

/**
 * Refresh access token if it expires within 60s. See BindDeviceModal for the
 * shared rationale; kept inline here to avoid premature abstraction.
 */
async function ensureFreshToken(auth) {
  const expiresAt = auth.user?.expires_at;
  const nowSec = Math.floor(Date.now() / 1000);
  if (!expiresAt || expiresAt - nowSec < 60) {
    try { await auth.signinSilent(); } catch { /* fall through */ }
  }
  return auth.user?.access_token;
}

const LicenseExtensionModal = ({ license, onClose, onExtend }) => {
  const [durationMonths, setDurationMonths] = useState('12');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const auth = useAuth();

  // Reset transient state on each open of the same instance.
  useEffect(() => {
    setDurationMonths('12');
    setError(null);
    setLoading(false);
  }, [license?.serialNumber]);

  const calculateExpiryPreview = () => {
    if (!license) return null;
    const baseDate = license.expiresAt && new Date(license.expiresAt) > new Date()
      ? new Date(license.expiresAt)
      : new Date();
    const previewDate = new Date(baseDate);
    previewDate.setMonth(previewDate.getMonth() + parseInt(durationMonths, 10));
    previewDate.setHours(23, 59, 59, 999);
    return previewDate;
  };

  const handleExtend = async () => {
    if (!license?.serialNumber) return;

    setLoading(true);
    setError(null);

    try {
      const token = await ensureFreshToken(auth);
      if (!token) throw new Error('Session expired. Please sign in again.');

      const duration = parseInt(durationMonths, 10);
      if (isNaN(duration) || duration <= 0 || duration > 60) {
        throw new Error('Duration must be between 1 and 60 months');
      }

      const result = await licenseService.extendLicense(license.serialNumber, duration, token);
      if (onExtend) onExtend(result);
      onClose();
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to extend license');
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString(undefined, {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getDaysRemaining = (expiryDate) => {
    if (!expiryDate) return 'N/A';
    const diffMs = new Date(expiryDate) - new Date();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return diffDays <= 0 ? 'Expired' : `${diffDays} days`;
  };

  if (!license) return null;

  const expiryPreview = calculateExpiryPreview();
  const isExtending = Boolean(license.expiresAt) && new Date(license.expiresAt) > new Date();
  const isExpired = license.expiresAt && new Date(license.expiresAt) <= new Date();
  const titleId = 'extend-license-modal-title';

  const statusVariant = !license.active ? 'inactive' : (isExpired ? 'warning' : 'active');
  const statusLabel = !license.active ? 'Inactive' : (isExpired ? 'Expired' : 'Active');

  return (
    <Modal
      isOpen
      onClose={onClose}
      labelledBy={titleId}
      size="lg"
      dismissable={!loading}
    >
      <Modal.Header id={titleId}>License Extension</Modal.Header>
      <Modal.Body>
        <section className="ext-modal__section">
          <h3 className="ext-modal__subtitle">License Details</h3>
          <dl className="ext-modal__details">
            <dt>Serial Number</dt>
            <dd className="ext-modal__mono">{license.serialNumber}</dd>

            <dt>License Tier</dt>
            <dd>{license.licenseTier || 'BASIC'}</dd>

            <dt>Customer</dt>
            <dd>{license.customerName || 'N/A'}</dd>

            <dt>Current Status</dt>
            <dd><Badge variant={statusVariant}>{statusLabel}</Badge></dd>

            <dt>Current Expiry</dt>
            <dd>{formatDate(license.expiresAt)}</dd>

            <dt>Days Remaining</dt>
            <dd>{getDaysRemaining(license.expiresAt)}</dd>
          </dl>
        </section>

        <section className="ext-modal__section">
          <h3 className="ext-modal__subtitle">Extension Period</h3>
          <div className="form-field form-field--inline">
            <label htmlFor="durationMonths" className="form-label">Duration</label>
            <select
              id="durationMonths"
              className="form-select"
              value={durationMonths}
              onChange={(e) => setDurationMonths(e.target.value)}
              disabled={loading}
            >
              <option value="1">1 month</option>
              <option value="3">3 months</option>
              <option value="6">6 months</option>
              <option value="12">12 months</option>
              <option value="24">24 months</option>
              <option value="36">36 months</option>
              <option value="60">60 months</option>
            </select>
          </div>

          <div className="ext-modal__summary">
            <p>
              {isExtending ? 'Extending' : 'Setting'} license by <strong>{durationMonths} months</strong>
              {isExtending ? ` from current expiry (${formatDate(license.expiresAt)})` : ' from today'}.
            </p>
            <p>
              <strong>New expiry date:</strong> {formatDate(expiryPreview)}
            </p>
            {license.extensionHistory && license.extensionHistory.length > 0 && (
              <p className="ext-modal__history">
                <em>This license has been extended {license.extensionHistory.length} time{license.extensionHistory.length === 1 ? '' : 's'} previously.</em>
              </p>
            )}
          </div>
        </section>

        {error && (
          <p role="alert" className="form-message form-message--error">{error}</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="ghost" onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="primary"
          onClick={handleExtend}
          disabled={loading}
          loading={loading}
        >
          Extend License
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default LicenseExtensionModal;
